import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, ChevronDown } from "lucide-react";
import { useState } from "react";
import webflowBadge from "@/assets/badges/webflow-certified-clean.svg";
import shopifyPartnersLogo from "@/assets/badges/shopify-partners-primary.svg";
import googleCloudLogo from "@/assets/badges/google-cloud-partner.svg";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import wavaiLogo from "@/assets/logo-wavai.svg";
import { useIsMobile } from "@/hooks/use-mobile";

const companyLinks = [
  { label: "About", labelAr: "من نحن", url: "/about" },
  { label: "Careers", labelAr: "وظائف", url: "/careers" },
  { label: "Contact", labelAr: "تواصل معنا", url: "/contact" },
  { label: "Blog", labelAr: "المدونة", url: "/blog" },
];

const serviceLinks = [
  { label: "Ecommerce", labelAr: "التجارة الإلكترونية", url: "/ecommerce" },
  { label: "Web Development", labelAr: "تطوير الويب", url: "/web" },
  { label: "AI Services", labelAr: "خدمات الذكاء الاصطناعي", url: "/ai" },
];

const platformLinks = [
  { label: "Shopify", labelAr: "شوبيفاي", url: "/shopify" },
  { label: "WordPress", labelAr: "ووردبريس", url: "/wordpress" },
  { label: "WooCommerce", labelAr: "ووكومرس", url: "/woocommerce" },
  { label: "Webflow", labelAr: "ويبفلو", url: "/webflow" },
];

const resourceLinks = [
  { label: "Solutions", labelAr: "الحلول", url: "/solutions" },
];

const partners = [
  { href: "https://www.shopify.com/partners/directory/partner/wavai-technology-llc", logo: shopifyPartnersLogo, alt: "Shopify Partners badge", height: "h-[1.86rem]" },
  { href: "https://webflow.com/@wavai", logo: webflowBadge, alt: "Webflow Certified Partner badge", height: "h-[1.6rem]" },
  { href: "https://cloud.google.com/find-a-partner/partner/wavai-technology", logo: googleCloudLogo, alt: "Google Cloud Partner badge", height: "h-6" },
];

function FooterColumn({
  heading,
  children,
  isMobile,
}: {
  heading: string;
  children: React.ReactNode;
  isMobile: boolean;
}) {
  const [open, setOpen] = useState(false);

  if (isMobile) {
    return (
      <div className="border-b border-primary-foreground/10 last:border-b-0">
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between py-4 text-left"
          aria-expanded={open}
        >
          <span className="text-xs font-semibold text-primary-foreground uppercase tracking-[0.15em]">
            {heading}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-primary-foreground/40 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && <div className="pb-4">{children}</div>}
      </div>
    );
  }

  return (
    <div>
      <span className="text-xs font-semibold text-primary-foreground uppercase tracking-[0.15em] block mb-4">
        {heading}
      </span>
      {children}
    </div>
  );
}

export function Footer() {
  const { lang, localePath } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);
  const isMobile = useIsMobile();

  const getLabel = (link: { label: string; labelAr: string }) =>
    lang === "ar" ? link.labelAr : link.label;

  const renderLinks = (links: { label: string; labelAr: string; url: string }[]) => (
    <ul className="space-y-3">
      {links.map((link) => (
        <li key={link.url}>
          <Link
            to={localePath(link.url)}
            className="text-sm text-primary-foreground/50 hover:text-primary-foreground transition-colors leading-relaxed"
          >
            {getLabel(link)}
          </Link>
        </li>
      ))}
    </ul>
  );

  return (
    <footer className="bg-foreground text-primary-foreground border-t border-primary-foreground/10">
      <div className="container px-6 py-14 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
          {/* Column 1 — Brand & Contact */}
          <div className="lg:col-span-4">
            <Link to={localePath("/")} className="inline-block mb-6" aria-label="Wavai homepage">
              <img src={wavaiLogo} alt="Wavai" className="h-7 w-auto brightness-0 invert" />
            </Link>

            <p className="text-sm text-primary-foreground/50 leading-relaxed mb-5">
              🇰🇼 {t("ACE Hardware Building, 2nd Floor")}
              <br />
              {t("Tunis Street, Hawally, Kuwait")}
            </p>

            <div className="flex flex-col gap-1 mb-6">
              <a
                href="mailto:hello@wavai.com"
                className="text-sm text-primary-foreground/50 hover:text-primary-foreground transition-colors w-fit"
              >
                hello@wavai.com
              </a>
              <a
                href="tel:+96597558522"
                dir="ltr"
                className="text-sm text-primary-foreground/50 hover:text-primary-foreground transition-colors w-fit"
              >
                +965 97558522
              </a>
            </div>

            {/* Social Icons */}
            <div className="flex items-center gap-3.5">
              <a href="https://www.facebook.com/wavaicom" target="_blank" rel="noopener noreferrer" aria-label="Follow us on Facebook" className="text-primary-foreground/50 hover:text-primary-foreground transition-colors">
                <Facebook className="w-[18px] h-[18px]" />
              </a>
              <a href="https://www.instagram.com/wavaicom" target="_blank" rel="noopener noreferrer" aria-label="Follow us on Instagram" className="text-primary-foreground/50 hover:text-primary-foreground transition-colors">
                <Instagram className="w-[18px] h-[18px]" />
              </a>
              <a href="https://x.com/wavaicom" target="_blank" rel="noopener noreferrer" aria-label="Follow us on X" className="text-primary-foreground/50 hover:text-primary-foreground transition-colors">
                <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
              </a>
              <a href="https://www.linkedin.com/company/wavai" target="_blank" rel="noopener noreferrer" aria-label="Follow us on LinkedIn" className="text-primary-foreground/50 hover:text-primary-foreground transition-colors">
                <Linkedin className="w-[18px] h-[18px]" />
              </a>
              <a href="https://www.tiktok.com/@wavaidotae" target="_blank" rel="noopener noreferrer" aria-label="Follow us on TikTok" className="text-primary-foreground/50 hover:text-primary-foreground transition-colors">
                <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.51a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.71a8.21 8.21 0 0 0 4.76 1.52V6.69h-1z" /></svg>
              </a>
              <a href="https://wa.me/+96597558522" target="_blank" rel="noopener noreferrer" aria-label="Message us on WhatsApp" className="text-primary-foreground/50 hover:text-primary-foreground transition-colors">
                <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
              </a>
            </div>
          </div>

          {/* Navigation columns */}
          <nav aria-label="Footer navigation" className="lg:col-span-5">
            <div className={`${isMobile ? "" : "grid grid-cols-3 gap-8"}`}>
              <FooterColumn heading={t("Company")} isMobile={isMobile}>
                {renderLinks(companyLinks)}
              </FooterColumn>

              <FooterColumn heading={t("Services")} isMobile={isMobile}>
                {renderLinks(serviceLinks)}
              </FooterColumn>

              <FooterColumn heading={t("Platforms")} isMobile={isMobile}>
                {renderLinks(platformLinks)}
                <div className="mt-3">
                  {renderLinks(resourceLinks)}
                </div>
              </FooterColumn>
            </div>

          </nav>

          {/* Column — Partners */}
          <div className="lg:col-span-3">
            <span className="text-xs font-semibold text-primary-foreground uppercase tracking-[0.15em] block mb-4">
              {t("Partners")}
            </span>
            <div className="flex flex-col gap-3">
              {partners.map((partner) => (
                <a
                  key={partner.alt}
                  href={partner.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-4 py-3 rounded-lg border border-primary-foreground/10 bg-primary-foreground/5 hover:bg-primary-foreground/10 transition-all duration-300"
                >
                  <img
                    src={partner.logo}
                    alt={partner.alt}
                    className={`${partner.height} w-auto brightness-0 invert`}
                  />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-10 pt-6 border-t border-primary-foreground/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-primary-foreground/40">
             © {new Date().getFullYear()} {t("Wavai. All rights reserved.")}
          </p>
          <div className="flex items-center gap-5">
            <Link to={localePath("/privacy-policy")} className="text-xs text-primary-foreground/40 hover:text-primary-foreground transition-colors">
              {t("Privacy Policy")}
            </Link>
            <Link to={localePath("/terms")} className="text-xs text-primary-foreground/40 hover:text-primary-foreground transition-colors">
              {t("Terms of Service")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
